/* ============================================================
   ARTIKEL LESSON — pemapar blok kandungan
   ============================================================
   Dipakai oleh halaman lesson (/tutorial/:videoId) untuk melukis
   `lesson.blocks`. Rupa ini DILULUSKAN user 2026-09-06 daripada
   lakaran /tutorial-artikel — jangan ubah bentuknya tanpa bertanya.

   ⭐ SATU TAKRIFAN, DUA TEMPAT. Editor (TutorialArtikelEditor)
      memaparkan blok yang SAMA melalui komponen ini, jadi apa yang
      admin nampak semasa menyunting ialah betul-betul apa yang
      pelanggan dapat. Kalau editor melukis rupanya sendiri, dua rupa
      itu PASTI terpesong dan admin akan menerbitkan sesuatu yang dia
      tidak pernah lihat.

   ⛔ LESSON TANPA BLOK TIDAK MELALUI FAIL INI LANGSUNG. Halaman lesson
      kekal rupa lamanya (video + satu kad teks) selagi `blocks` kosong,
      jadi setiap lesson sedia ada tidak berubah.

   CSS berskop awalan `.art-` — sifar sentuhan pada halaman lain.
   ============================================================ */

import { Check, Clock, Image as IkonGambar, Info, Lightbulb, TriangleAlert } from 'lucide-react';
import { idTajukBlok, kiraMasaBaca } from '../data/tutorial';
import { useT } from '../i18n';
import { pautkanTeks } from './artikelKongsi';


const IKON_NOTA = { tip: Lightbulb, info: Info, amaran: TriangleAlert, stop: TriangleAlert };

/* Satu blok. Dieksport supaya editor boleh memapar pratonton blok
   demi blok tanpa menyalin markup ini. */
export function BlokArtikel({ blok, indeks }) {
    switch (blok.jenis) {
        case 'tajuk':
            return <h2 className="art-h2" id={idTajukBlok(blok, indeks)}>{blok.teks}</h2>;

        case 'teks':
            return <p className="art-p">{pautkanTeks(blok.teks)}</p>;

        case 'langkah':
            return (
                <ol className="art-langkah">
                    {blok.items.filter(Boolean).map((it, i) => <li key={i}>{pautkanTeks(it)}</li>)}
                </ol>
            );

        case 'semak': {
            const senarai = (
                <ul className="art-semak">
                    {blok.items.filter(Boolean).map((it, i) => (
                        <li key={i}><Check size={16} strokeWidth={2.4} />{pautkanTeks(it)}</li>
                    ))}
                </ul>
            );
            /* Tanpa tajuk ia senarai biasa dalam aliran teks; dengan tajuk
               ia naik jadi kad berlabel (corak "Sebelum anda mula"). */
            if (!blok.tajuk) return <div style={{ margin: '18px 0' }}>{senarai}</div>;
            return (
                <div className="art-kad">
                    <div className="art-kad__t">{blok.tajuk}</div>
                    {senarai}
                </div>
            );
        }

        case 'gambar':
            return (
                <figure className="art-gbr">
                    {blok.url
                        ? <img src={blok.url} alt={blok.kapsyen || ''} loading="lazy" />
                        : (
                            <div className="art-gbr__kosong">
                                <span>
                                    <IkonGambar size={20} strokeWidth={1.6} style={{ display: 'block', margin: '0 auto 8px' }} />
                                    Gambar belum dimuat naik
                                </span>
                            </div>
                        )}
                    {blok.kapsyen && <figcaption className="art-gbr__cap">{blok.kapsyen}</figcaption>}
                </figure>
            );

        case 'nota': {
            const Ikon = IKON_NOTA[blok.gaya] || Lightbulb;
            const kelas = blok.gaya === 'stop' ? ' art-nota--stop' : blok.gaya === 'amaran' ? ' art-nota--amaran' : '';
            return (
                <div className={`art-nota${kelas}`}>
                    <Ikon size={17} strokeWidth={2} />
                    <div className="art-nota__b">
                        {blok.tajuk && <strong className="art-nota__t">{blok.tajuk}</strong>}
                        {pautkanTeks(blok.teks)}
                    </div>
                </div>
            );
        }

        case 'jadual':
            return (
                <table className="art-jad">
                    {(blok.kepala[0] || blok.kepala[1]) && (
                        <thead><tr><th>{blok.kepala[0]}</th><th>{blok.kepala[1]}</th></tr></thead>
                    )}
                    <tbody>
                        {blok.baris.filter(r => r[0] || r[1]).map((r, i) => (
                            <tr key={i}><td>{r[0]}</td><td>{pautkanTeks(r[1])}</td></tr>
                        ))}
                    </tbody>
                </table>
            );

        case 'sj':
            return (
                <div className="art-sj">
                    {blok.items.filter(x => x.s).map((x, i) => (
                        <details key={i}>
                            <summary>{x.s}</summary>
                            <p>{pautkanTeks(x.j)}</p>
                        </details>
                    ))}
                </div>
            );

        default:
            return null;
    }
}

export default function TutorialArtikel({ lesson }) {
    const t = useT();
    const blocks = lesson?.blocks || [];
    const tajukBlok = blocks
        .map((b, i) => ({ b, i }))
        .filter(x => x.b.jenis === 'tajuk' && x.b.teks);

    return (
        <div className="art">
            {lesson.body && <p className="art-lead">{pautkanTeks(lesson.body)}</p>}

            <div className="art-meta">
                <span><Clock size={14} strokeWidth={1.9} /> {t('tutorial.art.readTime', { n: kiraMasaBaca(lesson) })}</span>
            </div>

            {/* Senarai kandungan muncul SENDIRI bila artikel cukup panjang
                untuk memerlukannya. Di bawah tiga tajuk ia hanya mengulang
                apa yang sudah kelihatan tanpa skrol. */}
            {tajukBlok.length >= 3 && (
                <div className="art-kad">
                    <div className="art-kad__t">{t('tutorial.art.toc')}</div>
                    <ul className="art-toc">
                        {tajukBlok.map(({ b, i }) => (
                            <li key={b.id}><a href={`#${idTajukBlok(b, i)}`}>{b.teks}</a></li>
                        ))}
                    </ul>
                </div>
            )}

            {blocks.map((b, i) => <BlokArtikel key={b.id} blok={b} indeks={i} />)}
        </div>
    );
}
